import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    IconButton
} from '@mui/material';
import { Edit, Upload } from '@mui/icons-material';
import {
    confirmArticleCoverUpload,
    getPresignedArticleCoverUrl
} from '../../api-admin/tenant-articles';
import { s3Upload } from '../../api-admin/s3-upload';
import toastMessage from '../../utils/toast';

const ALLOWED_MIME_TYPES = ['image/png', 'image/jpeg', 'image/jpg'];
const MAX_FILE_SIZE_MB = 10;

export async function uploadArticleCover(
    tenantId: number | string,
    articleId: number | string,
    file: File
) {
    const mimeType = file.type;
    const { presignedUrl } = await getPresignedArticleCoverUrl(tenantId, articleId, { mimeType });
    await s3Upload({ file, presignedUrl, mimeType });
    const updated = await confirmArticleCoverUpload(tenantId, articleId, { mimeType });
    return updated.coverImageUrl || '';
}

type PropsType = {
    tenantId: number | string;
    articleId?: number | string;
    url?: string | null;
    onUploaded?: (coverImageUrl: string) => void | Promise<void>;
    onFileSelected?: (file: File) => void;
};

export default function ArticleCoverHandler({
    tenantId,
    articleId,
    url,
    onUploaded,
    onFileSelected
}: PropsType) {
    const [isUploading, setIsUploading] = useState(false);
    const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null);
    const hiddenFileInput = useRef<HTMLInputElement>(null);
    const localPreviewUrlRef = useRef<string | null>(null);

    useEffect(() => () => {
        if (localPreviewUrlRef.current) {
            URL.revokeObjectURL(localPreviewUrlRef.current);
        }
    }, []);

    const replaceLocalPreview = (file: File) => {
        if (localPreviewUrlRef.current) {
            URL.revokeObjectURL(localPreviewUrlRef.current);
        }
        const nextUrl = URL.createObjectURL(file);
        localPreviewUrlRef.current = nextUrl;
        setLocalPreviewUrl(nextUrl);
    };

    const handleClick = () => {
        if (isUploading) return;
        hiddenFileInput.current?.click();
    };

    const handleImgUpload = async () => {
        const input = hiddenFileInput.current;
        const file = input?.files?.[0];
        if (input) {
            input.value = '';
        }

        if (!file) {
            toastMessage('Ingen fil vald');
            return;
        }

        const mimeType = file.type;
        if (!mimeType || !ALLOWED_MIME_TYPES.includes(mimeType)) {
            toastMessage('Använd PNG eller JPG.');
            return;
        }

        if (file.size / 1024 / 1024 > MAX_FILE_SIZE_MB) {
            toastMessage('Filen måste vara mindre än 10MB');
            return;
        }

        if (!articleId) {
            replaceLocalPreview(file);
            onFileSelected?.(file);
            return;
        }

        setIsUploading(true);
        try {
            const coverImageUrl = await uploadArticleCover(tenantId, articleId, file);
            replaceLocalPreview(file);
            await onUploaded?.(coverImageUrl);
            toastMessage('Omslagsbilden har uppdaterats');
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error);
            toastMessage('Ett fel uppstod vid uppladdningen, försök igen senare');
        } finally {
            setIsUploading(false);
        }
    };

    const displayUrl = localPreviewUrl || url;

    return (
        <>
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <Box
                    sx={{
                        width: 160,
                        height: 120,
                        borderRadius: 1,
                        overflow: 'hidden',
                        bgcolor: 'action.hover',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    {displayUrl ? (
                        <Box
                            component="img"
                            src={displayUrl}
                            alt="Omslag"
                            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                    ) : (
                        <Upload />
                    )}
                </Box>
                <IconButton
                    size="small"
                    onClick={handleClick}
                    disabled={isUploading}
                    sx={{
                        position: 'absolute',
                        bottom: 4,
                        right: 4,
                        bgcolor: 'background.paper'
                    }}
                >
                    {isUploading ? <CircularProgress size={16} /> : <Edit fontSize="small" />}
                </IconButton>
            </Box>
            <Button onClick={handleClick} disabled={isUploading} sx={{ ml: 2 }}>
                {displayUrl ? 'Byt omslagsbild' : 'Ladda upp omslagsbild'}
            </Button>
            <input
                type="file"
                accept="image/png,image/jpeg"
                ref={hiddenFileInput}
                style={{ display: 'none' }}
                onChange={handleImgUpload}
            />
        </>
    );
}
